/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { Box, Button, CircularProgress, type SelectChangeEvent, Typography } from '@mui/material'
import AlertMessage from 'Components/AlertMessage'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { CheckBoxWithLabel } from 'Components/CheckBox'
import { type SelectOption, SelectWithLabel } from 'Components/SelectWithLabel'
import { LabelWithToolTip } from 'Components/Label/Index'
import { useEffect, useState } from 'react'
import { sendGet } from 'hooks/use-fetch'
import { toast } from 'react-toastify'
import { Modal } from 'Components/Modal'

function Layout (props: { banner: boolean, show: boolean, onCancel: () => void, children: JSX.Element }): JSX.Element {
  if (props.banner) {
    return <Modal
      hideTitle={true}
      maxWidth={ 'sm'}
      className={'Nested'}
      titleContent={<></>}
      sx={{
        position: 'absolute',
        bottom: 0
      }}
      contentStyles={{ p: '0px' }}
      onClose={props.onCancel}
      onConfirm={() => {}}
      open={props.show}
      hideXtoClose={true}
      confirmationContent={props.children}
      noButtons={true}
    />
  } else {
    return <>{props.children}</>
  }
}
interface defaultProps {
  cancel: () => void
  enrollmentId: number | null
  programId: number | null
  timeSpanId: number | null
  providerId: number | null
  isdid: number
  childId: number
  serviceId: number
  submitted: (isdSpId: number, serviceProviderId: number, programId: number, programName: string, timeSpanId: number, rank: number | null, serivceProviderName: string) => void
  variant: 'banner' | 'card'
  restrictProvider: boolean
  show?: boolean
}
type ProgramSelectProps = & defaultProps
export function ProgramSelect (props: ProgramSelectProps): JSX.Element {
  const [providerOptions, setProviderOptions] = useState<Array<{ serviceProviderID: number, id: number, serviceProviderName: string }>>([])
  const mappedProviderOptions: Array<SelectOption<string>> = providerOptions.map(p => { return { id: p.serviceProviderID.toString(), name: p.serviceProviderName } })
  const [programOptions, setProgramOptions] = useState<Array<SelectOption<string>>>([])
  const [timeSpanOptions, setTimeSpanOptions] = useState<Array<SelectOption<string>>>([])
  const [confirmed, setConfirmed] = useState(false)
  const [loadedData, setLoadedData] = useState(false)
  const [concurrencyValidation, setConcurrencyValidation] = useState<{ isValid: boolean
    rank: number | null
    rankWording: string | null }>({ isValid: true, rank: null, rankWording: null })
  const [concurrencyLoading, setConcurrencyLoading] = useState(false)
  const init = {
    programId: props.programId,
    schoolYearId: props.timeSpanId,
    providerId: props.providerId,
    rank: 1
  }
  const [currentData, setCurrentData] = useState<{ providerId: number | null, schoolYearId: number | null, programId: number | null, rank: number | null }>(init)
  const selectedProvider = providerOptions.filter(p => p.serviceProviderID === currentData.providerId)
  const isDirty = JSON.stringify(currentData) !== JSON.stringify(init)
  useEffect(() => {
    if (props.show ?? false) {
      setCurrentData(init)
    }
  }, [props.show])
  useEffect(() => {
    const loadData = async (): Promise<void> => {
      const { response } = await sendGet(`/NewEnrollment/GetProviders?isdid=${props.isdid}&serviceId=${props.serviceId}`, {})
      setProviderOptions(response)
      setLoadedData(true)
    }
    void loadData()
  }, [props.isdid, props.serviceId])
  useEffect(() => {
    const loadData = async (): Promise<void> => {
      if (currentData.providerId == null || currentData.schoolYearId == null) { return }
      const { response } = await sendGet(`/NewEnrollment/GetPrograms?isdid=${props.isdid}&serviceId=${props.serviceId}` +
        `&serviceProviderId=${currentData.providerId}&childId=${props.childId}&timeSpanId=${currentData.schoolYearId}`, {})
      const values: Array<SelectOption<number>> = response
      const programStrVals: Array<SelectOption<string>> = values.map((p: SelectOption<number>) => {
        const newOption: SelectOption<string> = { id: p.id.toString(), name: p.name }
        return newOption
      })
      setProgramOptions(programStrVals)
      setLoadedData(true)
    }
    void loadData()
  }, [props.isdid, props.serviceId, currentData.providerId, currentData.schoolYearId])
  useEffect(() => {
    const loadData = async (): Promise<void> => {
      if (currentData.providerId == null) { return }
      const { response } = await sendGet(`/NewEnrollment/GetTimeSpans?isdid=${props.isdid}&serviceId=${props.serviceId}` +
        `&serviceProviderId=${currentData.providerId}`, {})
      const values: Array<SelectOption<number>> = response
      const timespanStrVals: Array<SelectOption<string>> = values.map((p: SelectOption<number>) => {
        const newOption: SelectOption<string> = { id: p.id.toString(), name: p.name }
        return newOption
      })
      setTimeSpanOptions(timespanStrVals)
      setLoadedData(true)
    }
    void loadData()
  }, [props.isdid, props.serviceId, currentData.providerId])

  useEffect(() => {
    const validateConcurrency = async (): Promise<void> => {
      setConcurrencyLoading(true)
      const { response } = await sendGet(`/NewEnrollment/ValidateConcurrency?isdid=${props.isdid}&programId=${currentData?.programId}
        &childId=${props.childId}&timeSpanId=${currentData?.schoolYearId}&enrollmentId=${props.enrollmentId}`, {})
      const vals: { status: string, rank?: number } = response
      let isValid = false
      let caseDisplay = null
      let rank: number | null = null
      if (vals.status === 'Allowed') {
        isValid = true
      } else if (vals.status === 'NotAllowed') {
        isValid = false
        toast.error('You are not allowed to create concurrent enrollments')
      } else {
        isValid = true
        const place = (vals.rank ?? 1)
        switch (place) {
          case 1:
            caseDisplay = '1st'
            break
          case 2:
            caseDisplay = '2nd'
            break
          case 3:
            caseDisplay = '3rd'
            break
          default:
            caseDisplay = `${place}th`
            break
        }
        rank = vals.rank ?? 1
      }
      setConcurrencyValidation({ isValid, rank, rankWording: caseDisplay })
      setConcurrencyLoading(false)
    }
    if (
      (currentData?.programId ?? 0) !== 0 && (currentData?.schoolYearId ?? 0) !== 0 && (currentData.schoolYearId !== init.schoolYearId || currentData.programId !== init.programId)
    ) {
      void validateConcurrency()
    } else {
      setConcurrencyValidation({ isValid: true, rank: null, rankWording: null })
    }
  }, [currentData?.programId, currentData?.schoolYearId])

  const submit = async (): Promise<void> => {
    if (props.enrollmentId != null && !(confirmed ?? false)) {
      toast.error('Please confirm your selection')
      return
    }
    if (currentData?.programId == null || currentData?.programId === 0 || currentData?.schoolYearId == null || currentData?.schoolYearId === 0) {
      toast.error('Please select a program and school year')
      return
    }
    if (currentData?.providerId == null) {
      toast.error('Please select a provider')
      return
    }
    const filteredItem = programOptions.filter(i => i.id === (currentData.programId?.toString() ?? '0'))
    let str = ''
    if (filteredItem != null && filteredItem.length > 0) {
      str = filteredItem[0].name
    }

    props.submitted(selectedProvider[0].id, selectedProvider[0].serviceProviderID, currentData?.programId, str, currentData?.schoolYearId, concurrencyValidation.rank, selectedProvider[0].serviceProviderName)
  }
  const cancel = (): void => {
    props.cancel()
  }

  const updateProgram = (evt: SelectChangeEvent<string>): void => {
    const intVal = Number.parseInt(evt.target.value.toString())
    if (currentData == null) { return }
    setCurrentData({ ...currentData, programId: intVal })
    setConfirmed(false)
  }
  const updateProvider = (evt: SelectChangeEvent<string>): void => {
    const intVal = Number.parseInt(evt.target.value.toString())
    if (currentData == null) { return }
    setConfirmed(false)
    setConcurrencyValidation({ isValid: true, rank: null, rankWording: null })
    setCurrentData({ ...currentData, providerId: intVal, programId: null, schoolYearId: null })
    setLoadedData(false)
  }
  const updateSchoolYear = (evt: SelectChangeEvent<string>): void => {
    const intVal = Number.parseInt(evt.target.value.toString())
    if (currentData == null) { return }
    setConfirmed(false)
    setConcurrencyValidation({ isValid: true, rank: null, rankWording: null })
    setCurrentData({ ...currentData, schoolYearId: intVal, programId: null })
    setLoadedData(false)
  }
  const updateConfirm = (val: boolean): void => {
    if (currentData == null) { return }
    setConfirmed(val)
  }
  return <Layout banner={props.variant === 'banner'} show={props.show ?? false} onCancel={props.cancel}>
    <Box sx={{ p: '12px' }}>
      <Box sx={{ p: props.variant === 'banner' ? '0px 10px 20px 10px' : '20px 10px' }}>
        <Typography fontWeight={'bold'}>Change Program / School Year</Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {concurrencyValidation.rankWording != null && (
          <AlertMessage
            message={`This enrollment will be pending release. It will be ${concurrencyValidation.rankWording} in line.`}
            type={'warning'}
            dismissible={false}
            icon={<ErrorOutlineIcon />}
          />

        )}
        {props.enrollmentId != null && concurrencyValidation.isValid && (
          <AlertMessage
            message={'Changing the Program or School Year will reset the contents of this enrollment and you will have to re-include any added documents. Confirm below to proceed.'}
            type={'warning'}
            dismissible={false}
            icon={<ErrorOutlineIcon />}
          />
        )}
        {!concurrencyValidation.isValid && (
          <AlertMessage
            message={'You are not allowed to enter concurrent enrollments for this program.'}
            type={'error'}
            dismissible={false}
            icon={<ErrorOutlineIcon />}
          />
        )}
        {selectedProvider[0] != null && loadedData && currentData.schoolYearId != null && programOptions.length === 0 && (
          <AlertMessage message={'The current service provider does not have any programs configured for this child\'s age for this service.'}
            type='error'
            dismissible={false} icon={<ErrorOutlineIcon />}
          />
        )}
        {!props.restrictProvider && selectedProvider[0] != null && !providerOptions.some(p => p.serviceProviderID === selectedProvider[0].serviceProviderID) && (
          <AlertMessage message='The current service provider is unable to provide the selected service.' type='error'
            dismissible={false} icon={<ErrorOutlineIcon />}
          />
        )}

      </Box>
      {props.restrictProvider && (
        <>

          <Typography>{selectedProvider[0]?.serviceProviderName}</Typography>
        </>
      )}
      {!props.restrictProvider && (
        <SelectWithLabel label='Service Provider'

          options={mappedProviderOptions ?? []}
          value={currentData?.providerId?.toString() ?? ''}
          onChange={updateProvider} name={'providerId'} width='100%'
        />
      )}
      <Box sx={{ py: '20px', display: 'flex', flexDirection: 'row', gap: 2 }}>
        <Box>
          <LabelWithToolTip labelText='What Time Span Year is this enrollment for?' toolTipText='This is the period of time you want to enroll this child.  Typically this is a school year but it can be other things depending on the program and service.' />
          <SelectWithLabel options={timeSpanOptions} value={currentData?.schoolYearId?.toString() ?? ''} onChange={updateSchoolYear} name={'schoolYearId'} width='100%'/>
        </Box>
        <Box>
          <LabelWithToolTip labelText='What program is this enrollment for?' toolTipText='Only programs that the child is age eligible for will appear in this list.  The calculation is based on the age of the child at the latest time of enrollment (12/1). For more information on programs, refer to your local ISD’s guidelines for the age eligibility of children.' />
          <SelectWithLabel options={programOptions} value={currentData?.programId?.toString() ?? ''} onChange={updateProgram} name={'programId'} width='100%'/>
        </Box>
      </Box>

      <Box sx={{ pt: '30px', px: '10px', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <Button sx={{ textTransform: 'none' }} variant='outlined' onClick={cancel}>Cancel</Button>
        {props.enrollmentId != null && (
          <CheckBoxWithLabel name={'confirm'} disabled={!isDirty} label={isDirty ? 'Confirm changes' : 'No Changes'} value={ confirmed} setValue={updateConfirm } edge={'start'}/>
        )}
        {concurrencyLoading && <CircularProgress />}
        <Button sx={{ textTransform: 'none' }} variant='contained' disabled={concurrencyLoading || (props.enrollmentId != null && !confirmed) || !concurrencyValidation.isValid} onClick={submit}>Save Changes</Button>
      </Box>
    </Box>
  </Layout>
}
